import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { KeyValuePipe } from '@angular/common';

// Components
import { LoadingButtonComponent } from "../../../shared/components/loading-button/loading-button.component";

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// Services
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';


@Component({
  selector: 'app-otp',
  imports: [
    ReactiveFormsModule,
    LoadingButtonComponent,
    FontAwesomeModule,
    KeyValuePipe
  ],
  templateUrl: './otp.component.html',
  styleUrl: './otp.component.scss',
})
export class OtpComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly authSrv = inject(AuthService);
  private readonly toastSrv = inject(ToastService);

  readonly email = input.required<string>();
  readonly action = input.required<'LOGIN' | 'FORGOT_PASSWORD'>();
  readonly loading = input<boolean>(false);

  otpValidated = output();
  otpExpired = output<void>();

  otpProcessFailed = false;
  isOTPExpired = false;

  totalSeconds = signal(0);

  readonly minutesSeconds = computed(() => {
    const total = this.totalSeconds();
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  });

  otpForm: FormGroup = this.fb.group({
    otp0: ['', [Validators.required, Validators.pattern('[0-9]')]],
    otp1: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('[0-9]')]],
    otp2: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('[0-9]')]],
    otp3: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('[0-9]')]],
    otp4: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('[0-9]')]],
    otp5: [{ value: '', disabled: true }, [Validators.required, Validators.pattern('[0-9]')]],
  });

  constructor() {
    // Effetto reattivo: ogni volta che totalSeconds cambia, crea un nuovo timer
    effect((onCleanup) => {
      const initialSeconds = this.totalSeconds();

      // se è zero o negativo, non serve far partire un nuovo timer
      if (initialSeconds <= 0) return;

      // timestamp assoluto di scadenza (robusto al cambio scheda)
      const expiresAt = Date.now() + initialSeconds * 1000;

      // signal "clock": serve solo a triggerare il ricalcolo
      const now = signal(Date.now());

      // secondi rimanenti calcolati in modo deterministico
      const remaining = computed(() =>
        Math.max(
          0,
          Math.ceil((expiresAt - now()) / 1000)
        )
      );

      const intervalId = setInterval(() => {
        // aggiorna solo il clock (nessuna logica temporale)
        now.set(Date.now());

        // sincronizza il valore esposto
        this.totalSeconds.set(remaining());

        if (remaining() <= 0) {
          this.otpExpired.emit();
          this.isOTPExpired = true;
          clearInterval(intervalId);
          console.debug('OTP expired');
        }
      }, 1000);

      // cleanup automatico quando il signal cambia o il componente viene distrutto
      onCleanup(() => clearInterval(intervalId));
    });
  }

  ngOnInit(): void {
    const otpInfo = this.authSrv.getOtpInfo()();
    if (otpInfo) {
      // Imposta inizialmente il tempo di scadenza
      const now = new Date();
      const expiresAt = new Date(otpInfo.expiresAt);
      const seconds = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / 1000));
      this.totalSeconds.set(seconds);
    }
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const nextInput = input.nextElementSibling as HTMLInputElement;

    if (input.value.length > 1) {
      input.value = input.value.charAt(0);
    }

    if (input.value && nextInput) {
      const nextControlName = `otp${index + 1}`;
      this.otpForm.get(nextControlName)?.enable();
      nextInput.focus();
    }
  }

  onBackspace(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    const prevInput = input.previousElementSibling as HTMLInputElement;
    const wasEmpty = input.value === '';  // flag se il campo era già vuoto

    if (event.key === 'Backspace') {
      // 1) Svuota il campo corrente
      this.otpForm.get(`otp${index}`)?.setValue('');

      // 2) Disabilita tutti i campi successivi
      for (let i = index + 1; i < 6; i++) {
        const ctrl = this.otpForm.get(`otp${i}`);
        ctrl?.disable();
        ctrl?.setValue('');
      }

      // 3) Se il campo era già vuoto, allora sposta il focus al precedente
      if (wasEmpty && prevInput) {
        this.otpForm.get(`otp${index - 1}`)?.enable();
        prevInput.focus();
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const values = pastedData.split('');

    values.forEach((char, index) => {
      if (index < 6) {
        const control = `otp${index}`;
        this.otpForm.get(control)?.setValue(char);
        this.otpForm.get(control)?.enable();
      }
    });
  }

  getOtp(): string {
    return Object.values(this.otpForm.controls).map(control => control.value).join('');
  }

  validateOtp() {
    if (!this.otpForm.valid) return;

    const otpCode = this.getOtp();
    const action = this.action();

    this.authSrv.verifyOTPCode(otpCode).subscribe({
      next: (res) => {
        if (!res.valid) {
          this.otpProcessFailed = true;
          this.toastSrv.show({
            type: 'error',
            message: 'Codice OTP non valido. Richiedere un nuovo codice.'
          });
          return;
        }

        this.authSrv.setOtp(otpCode);
        this.otpValidated.emit();
      }
    });
  }


  resendOTP() {
    this.authSrv.resendOTP(this.email(), this.isOTPExpired || this.otpProcessFailed)
      .subscribe(
        {
          next: (res) => {
            console.debug('OTP Resent!');
            this.totalSeconds.set(0);
            this.isOTPExpired = false;
            this.totalSeconds.set(res.expiresInSeconds);
            this.toastSrv.show({ type: 'success', message: 'OTP re-inviato con successo' });
          },
          error: (err) => {
            this.otpExpired.emit();
          }
        });
  }
}
